import { GitCompare } from 'lucide-react';
import AboutMenu from './AboutMenu';
import CopyMenu from './CopyMenu';
import ExportMenu from './ExportMenu';
import InterfaceThemeToggle from './InterfaceThemeToggle';
import LayoutMenu from './LayoutMenu';
import NewMenu from './NewMenu';
import RecentMenu from './RecentMenu';
import ShareMenu from './ShareMenu';
import ThemeMenu from './ThemeMenu';
import VimModeToggle from './VimModeToggle';
import type { DiagramType } from '../lib/diagramTypes';
import type { HistoryRow } from '../lib/history';
import type { DiagramTheme, Look } from '../lib/mermaidRender';

/**
 * SPEC.md §3, §11 — the topbar, 52px tall at desktop widths.
 *
 * M0 shipped every control present but disabled; they light up in the
 * milestone that gives them something to do. Issue #16 (M4.2) was New's
 * turn; issue #22 (M5.2) was Export's; issue #24 (M5.4) was Copy's; issue
 * #29 (M6.2) was Share's; issue #33 (M6.6) was Theme's and Layout's; issue
 * #34 (M6.7) adds the interface theme toggle, positioned by About since
 * both are chrome/meta concerns rather than diagram controls. Issue #37
 * (M6.10) adds `flex-wrap`, but only while `isNarrow` — below 820px there
 * isn't room for every control on one line, and wrapping onto a second (or
 * third) row is the ticket's own explicit choice over clipping or
 * horizontal scrolling. Gated (not unconditional): every control laid out
 * end to end genuinely needs more than 820px, so an always-on `flex-wrap`
 * would start wrapping around ~850px — inside the "above 820px, behavior
 * is pixel-identical to today" AC's own territory. `isNarrow` is App.tsx's
 * one source of truth for the 820px breakpoint, threaded here rather than
 * this file guessing at its own CSS-only threshold. App.tsx's
 * `minmax(52px, auto)` topbar grid row (not a bare `52px`) is what actually
 * gives a wrapped row the height to render in without being cut off.
 */

interface TopbarProps {
  onSelectTemplate: (type: DiagramType) => void;
  onRestoreFromHistory: (row: HistoryRow) => void;
  source: string;
  svg: string | null;
  diagramType: DiagramType | null;
  diagramTheme: DiagramTheme;
  look: Look;
  onDiagramThemeChange: (diagramTheme: DiagramTheme) => void;
  onLookChange: (look: Look) => void;
  onExport: (message: string) => void;
  onCopy: (message: string) => void;
  onClearAllData: () => void;
  onOpenDiff: () => void;
  /** SPEC.md §11, issue #37: same 820px breakpoint App.tsx uses to switch the pane layout — gates `flex-wrap` so wrapping never starts above it. */
  isNarrow: boolean;
}

export default function Topbar({
  onSelectTemplate,
  onRestoreFromHistory,
  source,
  svg,
  diagramType,
  diagramTheme,
  look,
  onDiagramThemeChange,
  onLookChange,
  onExport,
  onCopy,
  onClearAllData,
  onOpenDiff,
  isNarrow,
}: TopbarProps) {
  return (
    <header className={`flex items-center gap-1 border-b border-ink-3 bg-ink-2 px-3 ${isNarrow ? 'flex-wrap' : ''}`}>
      <span className="mr-3 font-display text-base font-extrabold tracking-tight text-text-ink">
        plotter
      </span>

      <NewMenu onSelect={onSelectTemplate} />
      <RecentMenu onRestore={onRestoreFromHistory} />
      <ThemeMenu diagramTheme={diagramTheme} onSelect={onDiagramThemeChange} />
      <LayoutMenu look={look} onSelect={onLookChange} />
      <button
        type="button"
        onClick={onOpenDiff}
        className="flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-text-ink hover:bg-ink-3"
      >
        <GitCompare size={13} aria-hidden />
        Compare
      </button>

      <div className="grow" />

      <CopyMenu source={source} svg={svg} onCopy={onCopy} />
      <ShareMenu source={source} diagramTheme={diagramTheme} look={look} onCopy={onCopy} />
      <ExportMenu svg={svg} diagramType={diagramType} onExport={onExport} />
      <AboutMenu onClearAllData={onClearAllData} />
      <VimModeToggle />
      <InterfaceThemeToggle />
    </header>
  );
}
